import type { DeploymentEniOdb, EniOdbUnit } from "../../api/types";

/** Unités de l'ordre de bataille ennemi du dossier dans les listes (docs/14 §7.10b) : fonctions pures. */

/** Profondeur la plus basse des unités à glisser depuis la liste : formations et leurs subordonnés directs (une
 *  brigade et ses bataillons, un bataillon et ses compagnies, une compagnie et ses sections) ; les groupes et les
 *  niveaux plus fins ne sont jamais cités par l'ordre. */
export const PLACEABLE_DEPTH = 2;

/** Ligne de la liste : unité de l'ordre de bataille, sur la carte ou à glisser. */
export interface EniListRow {
  unit: EniOdbUnit;
  /** Non placée, à glisser sur la carte. */
  placeable: boolean;
}

/** Unités listées, dans l'ordre de l'arbre : celles qui sont sur la carte (emplacement de la manœuvre ou posées
 *  depuis la liste), puis à leur rang celles qui peuvent y être glissées — non placées, sous la racine, à deux
 *  niveaux au plus, et ni formation ni subordonnée d'une unité déjà sur la carte (elle y figurerait deux fois). */
export function eniListRows(odb: DeploymentEniOdb | null | undefined): EniListRow[] {
  if (!odb) return [];
  const byCode = new Map(odb.units.map((u) => [u.code, u]));
  const ancestors = (unit: EniOdbUnit): EniOdbUnit[] => {
    const out: EniOdbUnit[] = [];
    const seen = new Set<string>([unit.code]);
    let parent = unit.parent ? byCode.get(unit.parent) : undefined;
    while (parent && !seen.has(parent.code)) {
      out.push(parent);
      seen.add(parent.code);
      parent = parent.parent ? byCode.get(parent.parent) : undefined;
    }
    return out;
  };
  // Formations d'une unité placée : elles la contiennent déjà.
  const holding = new Set<string>();
  for (const unit of odb.units) if (unit.placed) for (const a of ancestors(unit)) holding.add(a.code);
  const rows: EniListRow[] = [];
  for (const unit of odb.units) {
    if (unit.placed) {
      rows.push({ unit, placeable: false });
      continue;
    }
    const placeable =
      unit.depth > 0 &&
      unit.depth <= PLACEABLE_DEPTH &&
      !holding.has(unit.code) &&
      !ancestors(unit).some((a) => a.placed);
    if (placeable) rows.push({ unit, placeable: true });
  }
  return rows;
}

/** Nombre d'unités à glisser sur la carte. */
export function placeableCount(odb: DeploymentEniOdb | null | undefined): number {
  return eniListRows(odb).filter((r) => r.placeable).length;
}

/** Nom libre d'une unité quand il diffère de son code (l'ordre cite le code). */
export function freeName(unit: Pick<EniOdbUnit, "code" | "display_name">): string | null {
  const name = unit.display_name?.trim();
  return name && name !== unit.code ? name : null;
}

/** Unité de l'ordre de bataille ennemi posée depuis la liste (hors des emplacements de la manœuvre) : elle peut en
 *  être retirée (elle redevient « non placée »). */
export function removable(unit: Pick<EniOdbUnit, "slot" | "placed">): boolean {
  return unit.placed && unit.slot === null;
}

/** Ligne glissable sur la carte au temps affiché : unité « non placée », ou posée depuis la liste à un temps
 *  ultérieur et absente de la carte à ce temps (elle y est alors ajoutée, et aux temps suivants où elle manque). */
export function draggableAt(row: EniListRow, onMapAtPhase: boolean): boolean {
  return row.placeable || (removable(row.unit) && !onMapAtPhase);
}
